import {useForm, useWatch} from "react-hook-form";
import {currencyFormatter} from "../utils/helpers";
import {CrossSellModel} from "../models/domain/crossSellModel";
import {useMemo} from "react";
import {useAppContext} from "./useAppContext";
import {useCartService} from "./addToCart/useCartService";

type FormType = {
    items: number[]
};

const props = {defaultValues: {items: [] as number[]}};

export const useBundleForm = (crossSells: CrossSellModel[]) => {
    const {productId: currentProductId} = useAppContext();

    const {createItems} = useCartService();

    // Create a form with the default values set to the cross-sells passed in as props
    const form = useForm<FormType>(props);

    // Set the 'items' field to the cross-sells passed in as props when the component mounts
    useMemo(() => form.reset({items: crossSells.map(({product: {productId}}) => productId)}), [crossSells]);

    // Get the selected product ids from the form
    const selectedProductIds = useWatch({control: form.control, name: 'items'});

    // Calculate the price of the selected products using the selected product ids
    const price = useMemo(() => currencyFormatter.format(crossSells
        .filter(({product: {productId}}) => selectedProductIds.some((selectedProductId: number) => selectedProductId === productId))
        .reduce((runningTotal, {price: {price}}) => runningTotal + price, 0)), [crossSells, selectedProductIds]);

    const selectedCrossSells = useMemo(() => crossSells.filter(({product: {productId}}) => selectedProductIds.some((selectedProductId: number) => selectedProductId === productId)), [crossSells, selectedProductIds]);

    // Format the adding to cart button text based on the number of selected products
    const addToCartButtonText = useMemo(() => {
        switch (selectedProductIds.length) {
            case 1:
                return 'Add to Cart';
            case 2:
                return 'Add both to Cart';
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
            case 8:
            case 9:
            case 10:
                return `Add all ${selectedProductIds.length} to Cart`;
            default:
                return 'Add all to Cart';
        }
    }, [crossSells, form, price, selectedProductIds, selectedCrossSells]);

    // If there are no bundled items, excluding the current product, don't render anything
    const zeroBundledItems = useMemo(() => crossSells.filter(({product: {productId}}) => currentProductId !== productId).length === 0, [crossSells]);

    // Return the form and the price of the selected products
    return {
        form,
        onSubmit: form.handleSubmit(async () => {
            const productIds = crossSells.flatMap(model => model.product)
                .filter(item => selectedProductIds.includes(item.productId))
                .map(item => item.productId);

            await createItems(productIds);
        }),
        selected: {
            ids: selectedProductIds,
            crossSells: selectedCrossSells,
            price,
        },
        zeroBundledItems,
        addToCartButtonText
    }
}